import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PackageOpen, AlertTriangle, Plus, Minus, ArrowRight, Sprout } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

export default function BuildBox() {
  const [tiers, setTiers] = useState([]);
  const [items, setItems] = useState([]);
  const [tierKey, setTierKey] = useState(null);
  const [qty, setQty] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const { addCustomBox } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get('/pricing'), api.get('/build-box/items')])
      .then(([p, i]) => {
        setTiers(p.data.box_tiers);
        setItems(i.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const tier = tiers.find((t) => t.key === tierKey);

  const selected = useMemo(() => {
    const chosen = items
      .filter((it) => (qty[it.item_id] || 0) > 0)
      .map((it) => ({ item: it, quantity: qty[it.item_id] }));
    const itemCount = chosen.reduce((s, c) => s + c.quantity, 0);
    const weight = chosen.reduce((s, c) => s + c.item.weight_lb * c.quantity, 0);
    const total = chosen.reduce((s, c) => s + c.item.price * c.quantity, 0);
    return { chosen, itemCount, weight: Math.round(weight * 100) / 100, total: Math.round(total * 100) / 100 };
  }, [items, qty]);

  const overCapacity = tier && selected.weight > tier.weight_capacity_lb;
  const categories = [...new Set(items.map((i) => i.category))];

  const setItemQty = (itemId, delta) => {
    setQty((q) => ({ ...q, [itemId]: Math.max(0, (q[itemId] || 0) + delta) }));
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please sign in to build a box');
      navigate('/login');
      return;
    }
    if (selected.chosen.length === 0) {
      toast.error('Add at least one item to your box');
      return;
    }
    try {
      setSaving(true);
      await addCustomBox(tierKey, selected.chosen.map((c) => ({ item_id: c.item.item_id, quantity: c.quantity })));
      toast.success('Custom box added to cart!');
      navigate('/cart');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not add box to cart');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-cream flex items-center justify-center" data-testid="build-box-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-cream" data-testid="build-box-page">
      <section className="py-16 bg-dark relative overflow-hidden">
        <div className="container-custom relative z-10">
          <span className="inline-block badge-local mb-4">Build Your Own</span>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-cream mb-4" data-testid="build-box-title">
            Build Your Own Box
          </h1>
          <p className="font-manrope text-cream/70 text-lg max-w-2xl">
            Pick a size, then choose exactly what goes in your box. Every item is priced individually and sourced from a verified local farm or producer.
          </p>
        </div>
      </section>

      <div className="container-custom py-12">
        {/* Step 1: Tier */}
        <h2 className="font-playfair text-2xl font-bold text-dark mb-6">1. Choose a Box Size</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {tiers.map((t) => (
            <button
              key={t.key}
              onClick={() => setTierKey(t.key)}
              className={`text-left p-6 rounded-2xl border-2 transition-all ${tierKey === t.key ? 'border-forest bg-forest/5' : 'border-forest/10 bg-white hover:border-forest/30'}`}
              data-testid={`build-box-tier-${t.key}`}
            >
              <h3 className="font-playfair text-xl font-bold text-dark mb-1">{t.name}</h3>
              <p className="text-sm text-dark/60 font-manrope mb-3">{t.serving}</p>
              <p className="font-manrope text-sm text-forest font-medium">
                Guideline: {t.item_count_min}-{t.item_count_max} items · up to {t.weight_capacity_lb} lb
              </p>
            </button>
          ))}
        </div>

        {!tierKey ? (
          <p className="text-center text-dark/50 font-manrope py-12">Choose a box size above to start picking items.</p>
        ) : items.length === 0 ? (
          <div className="text-center py-24 bg-white rounded-2xl border border-forest/10" data-testid="build-box-empty-items">
            <Sprout className="w-16 h-16 text-dark/20 mx-auto mb-4" />
            <h3 className="font-playfair text-2xl font-bold text-dark mb-2">No verified items yet</h3>
            <p className="font-manrope text-dark/60 max-w-md mx-auto">
              We only list items from confirmed local farms and producers. Check back soon as farms join FarmBox, or{' '}
              <Link to="/shop" className="text-forest font-medium hover:underline">browse our ready-made boxes</Link>.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Item picker */}
            <div className="lg:col-span-2 space-y-8">
              <h2 className="font-playfair text-2xl font-bold text-dark">2. Pick Your Items</h2>
              {categories.map((cat) => (
                <div key={cat}>
                  <h3 className="font-manrope font-bold text-dark/70 uppercase text-xs tracking-widest mb-3">{cat}</h3>
                  <div className="space-y-3">
                    {items.filter((i) => i.category === cat).map((it) => (
                      <div key={it.item_id} className="bg-white rounded-xl border border-forest/10 p-4 flex items-center gap-4" data-testid={`build-box-item-${it.item_id}`}>
                        <img src={it.image_url} alt={it.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                        <div className="flex-grow">
                          <p className="font-manrope font-semibold text-dark">{it.name}</p>
                          <p className="text-xs text-dark/50 font-manrope">{it.size_note} · {it.weight_lb} lb · Sourced from {it.farm_name}, {it.farm_location}</p>
                          <p className="font-manrope font-bold text-forest">${it.price.toFixed(2)} / {it.unit}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button onClick={() => setItemQty(it.item_id, -1)} disabled={!qty[it.item_id]} className="w-8 h-8 rounded-full border border-forest/20 flex items-center justify-center disabled:opacity-40" data-testid={`decrement-${it.item_id}`}>
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="font-manrope font-bold w-6 text-center" data-testid={`qty-${it.item_id}`}>{qty[it.item_id] || 0}</span>
                          <button onClick={() => setItemQty(it.item_id, 1)} className="w-8 h-8 rounded-full border border-forest/20 flex items-center justify-center" data-testid={`increment-${it.item_id}`}>
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-8 border border-forest/10 sticky top-28" data-testid="build-box-summary">
                <div className="flex items-center gap-3 mb-4">
                  <PackageOpen className="w-6 h-6 text-forest" />
                  <h3 className="font-playfair text-xl font-bold text-dark">Your {tier?.name}</h3>
                </div>
                <div className="space-y-2 font-manrope text-sm mb-4">
                  <div className="flex justify-between"><span className="text-dark/60">Items</span><span data-testid="build-box-item-count">{selected.itemCount} <span className="text-dark/40">(guideline {tier?.item_count_min}-{tier?.item_count_max})</span></span></div>
                  <div className="flex justify-between"><span className="text-dark/60">Weight</span><span data-testid="build-box-weight">{selected.weight} lb <span className="text-dark/40">(up to {tier?.weight_capacity_lb} lb)</span></span></div>
                </div>
                {tier && (
                  <div className="w-full h-2 rounded-full bg-forest/10 mb-4 overflow-hidden">
                    <div className={`h-full ${overCapacity ? 'bg-red-400' : 'bg-forest'}`} style={{ width: `${Math.min(100, (selected.weight / tier.weight_capacity_lb) * 100)}%` }} />
                  </div>
                )}
                {overCapacity && (
                  <div className="flex items-start gap-2 bg-gold/10 border border-gold/30 rounded-xl p-3 mb-4" data-testid="build-box-capacity-warning">
                    <AlertTriangle className="w-4 h-4 text-gold-dark flex-shrink-0 mt-0.5" />
                    <p className="text-xs font-manrope text-dark/70">
                      This box is over the usual weight guideline for a {tier.name}. It's just a heads up &mdash; feel free to remove or swap items, or keep it if you're fine with a fuller box.
                    </p>
                  </div>
                )}
                <div className="border-t border-forest/10 pt-4 mb-6">
                  <div className="flex justify-between">
                    <span className="font-playfair text-xl font-bold text-dark">Box Total</span>
                    <span className="font-playfair text-xl font-bold text-dark" data-testid="build-box-total">${selected.total.toFixed(2)}</span>
                  </div>
                </div>
                <button
                  onClick={handleAddToCart}
                  disabled={saving || selected.chosen.length === 0}
                  className="btn-primary w-full justify-center"
                  data-testid="build-box-add-to-cart"
                >
                  {saving ? 'Adding...' : 'Add Box to Cart'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
